import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Assessment,
  PeopleAlt,
  Assignment,
  Receipt,
  Download,
  Refresh,
  Router,
  NetworkCheck,
  Message,
  TrendingUp,
} from '@mui/icons-material';

import { usePermissions } from '../contexts/AuthContext';
import { Client, Contract, Invoice } from '../types';
import { clientService } from '../services/clientService';
import { contractService } from '../services/contractService';
import invoiceService from '../services/invoiceService';
import { relatorioPdfService } from '../services/relatorioPdfService';
import { formatarCPFCNPJ, formatarData, formatarValor } from '../utils/formatToView';

import {
  RelatorioClientesTab,
  RelatorioContratosTab,
  RelatorioFaturasTab,
  RelatorioFaturamentoTab,
  RelatorioDiciTab,
  RelatorioConexoesTab,
  RelatorioMensagensTab,
  filtersClientesDefault,
  filtersContratosDefault,
  filtersFaturasDefault,
  filtersFaturamentoDefault,
  fmtInt,
  useRelatorioCores,
  getFirstAndLastDayOfMonth,
  REPORT_TAB_PERMISSIONS,
} from './relatorios';
import type { FiltersClientes, FiltersContratos, FiltersFaturas, FiltersFaturamento, ReportType } from './relatorios';
type OrderDir = 'asc' | 'desc';

const RelatoriosPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { can } = usePermissions();
  const escritorioId = searchParams.get('escritorio_id') || undefined;

  const theme = useTheme();
  const cores = useRelatorioCores();

  // Get first and last day of current month for default filters
  const { firstDay, lastDay } = getFirstAndLastDayOfMonth();

  const [reportType, setReportType] = useState<ReportType>('clientes');
  const [filtersClientes, setFiltersClientes] = useState<FiltersClientes>({ ...filtersClientesDefault });
  const [filtersContratos, setFiltersContratos] = useState<FiltersContratos>({ ...filtersContratosDefault });
  const [filtersFaturas, setFiltersFaturas] = useState<FiltersFaturas>({ ...filtersFaturasDefault, dataInicio: firstDay, dataFim: lastDay });
  const [filtersFaturamento, setFiltersFaturamento] = useState<FiltersFaturamento>({ ...filtersFaturamentoDefault, dataInicio: firstDay, dataFim: lastDay });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<string>('id');
  const [orderDir, setOrderDir] = useState<OrderDir>('desc');

  const updateFiltersClientes = useCallback((updates: Partial<FiltersClientes>) => {
    setFiltersClientes((prev) => ({ ...prev, ...updates }));
    setPage(0);
  }, []);

  const updateFiltersContratos = useCallback((updates: Partial<FiltersContratos>) => {
    setFiltersContratos((prev) => ({ ...prev, ...updates }));
    setPage(0);
  }, []);

  const updateFiltersFaturas = useCallback((updates: Partial<FiltersFaturas>) => {
    setFiltersFaturas((prev) => ({ ...prev, ...updates }));
    setPage(0);
  }, []);

  const clearFiltersClientes = useCallback(() => {
    setFiltersClientes({ ...filtersClientesDefault });
    setPage(0);
  }, []);

  const clearFiltersContratos = useCallback(() => {
    setFiltersContratos({ ...filtersContratosDefault });
    setPage(0);
  }, []);

  const clearFiltersFaturas = useCallback(() => {
    setFiltersFaturas({ ...filtersFaturasDefault });
    setPage(0);
  }, []);

  const updateFiltersFaturamento = useCallback((updates: Partial<FiltersFaturamento>) => {
    setFiltersFaturamento((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearFiltersFaturamento = useCallback(() => {
    setFiltersFaturamento({ ...filtersFaturamentoDefault, dataInicio: firstDay, dataFim: lastDay });
  }, [firstDay, lastDay]);

  const { data: clientsResp, isLoading: loadingClients, refetch: refetchClients } = useQuery({
    queryKey: ['report-clients', escritorioId],
    queryFn: () =>
      clientService.getAll({
        page: 1,
        limit: 5000,
        ...(escritorioId ? { escritorio_id: escritorioId } as any : {}),
      }),
    staleTime: 120_000,
    refetchOnWindowFocus: false,
    enabled: reportType === 'clientes' || reportType === 'faturas',
  });

  const { data: contractsResp, isLoading: loadingContracts, refetch: refetchContracts } = useQuery({
    queryKey: ['report-contracts', escritorioId, filtersContratos.statusContrato, filtersContratos.plano],
    queryFn: () =>
      contractService.getAll({
        page: 1,
        limit: 5000,
        filters: {
          ...(filtersContratos.statusContrato && { status: filtersContratos.statusContrato }),
          ...(filtersContratos.plano && { plano_id: Number(filtersContratos.plano) }),
          ...(escritorioId && { escritorio_id: Number(escritorioId) } as any),
        },
      }),
    staleTime: 120_000,
    refetchOnWindowFocus: false,
    enabled: reportType === 'contratos' || reportType === 'faturas' || reportType === 'clientes',
  });

  const { data: invoicesResp, isLoading: loadingInvoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['report-invoices', escritorioId, filtersFaturas.dataInicio, filtersFaturas.dataFim, filtersFaturas.dataPagamentoInicio, filtersFaturas.dataPagamentoFim, filtersFaturas.dataCriacaoInicio, filtersFaturas.dataCriacaoFim, filtersFaturas.statusFatura, filtersFaturas.tipoFatura],
    queryFn: () => invoiceService.getInvoices({
      page: 1,
      limit: 5000,
      data_inicio: filtersFaturas.dataInicio,
      data_fim: filtersFaturas.dataFim,
      data_pagamento_inicio: filtersFaturas.dataPagamentoInicio,
      data_pagamento_fim: filtersFaturas.dataPagamentoFim,
      data_criacao_inicio: filtersFaturas.dataCriacaoInicio,
      data_criacao_fim: filtersFaturas.dataCriacaoFim,
      ...(filtersFaturas.statusFatura && { status: filtersFaturas.statusFatura }),
      ...(filtersFaturas.tipoFatura && { tipo: filtersFaturas.tipoFatura }),
    }),
    staleTime: 120_000,
    refetchOnWindowFocus: false,
    enabled: reportType === 'faturas' || reportType === 'clientes' || reportType === 'contratos',
  });

  // QUERY GLOBAL DE INADIMPLÊNCIA: Busca faturas vencidas sem limite de data para conferência de status
  const { data: overdueInvoicesResp, isLoading: loadingOverdueInvoices } = useQuery({
    queryKey: ['report-overdue-invoices-all', escritorioId],
    queryFn: () => invoiceService.getInvoices({
      status: 'vencido',
      limit: 5000,
      ...(escritorioId ? { escritorio_id: escritorioId } : {}),
    } as any),
    staleTime: 120_000,
    enabled: true, // Sempre habilitado para garantir consistência de status
  });

  const allClients: Client[] = clientsResp?.data || [];
  const allContracts: Contract[] = contractsResp?.data || [];
  const allInvoices: Invoice[] = invoicesResp?.data || [];
  const allOverdueInvoices: Invoice[] = overdueInvoicesResp?.data || [];

  const filteredClients = useMemo(() => {
    const clientsWithOverdue = new Set<number>();
    allOverdueInvoices.forEach(inv => {
      clientsWithOverdue.add(inv.cliente_id);
    });

    const idsComContratosAtivos = new Set<number>();
    if (filtersClientes.statusCliente === 'inativo' && filtersClientes.comContratosAtivos) {
      allContracts.forEach(cont => {
        if (cont.status === 'ativo') {
          idsComContratosAtivos.add(cont.cliente_id);
        }
      });
    }

    return allClients
      .filter((c) => {
        const s = filtersClientes.search.toLowerCase();
        if (s && !c.nome.toLowerCase().includes(s) && !c.cpf_cnpj.includes(filtersClientes.search)) return false;

        if (filtersClientes.inadimplente && !clientsWithOverdue.has(c.id)) return false;

        if (filtersClientes.statusCliente) {
          if (c.status !== filtersClientes.statusCliente) {
            return false;
          }

          // Se status inativo e selecionado "Com contratos ativos"
          if (filtersClientes.statusCliente === 'inativo' && filtersClientes.comContratosAtivos && !idsComContratosAtivos.has(c.id)) {
            return false;
          }
        }

        if (filtersClientes.tipoPessoa && c.tipo_pessoa !== filtersClientes.tipoPessoa) return false;
        if (filtersClientes.estado && c.estado !== filtersClientes.estado) return false;
        if (filtersClientes.cidade && !c.cidade?.toLowerCase().includes(filtersClientes.cidade.toLowerCase())) return false;
        if (filtersClientes.bairro && !c.bairro?.toLowerCase().includes(filtersClientes.bairro.toLowerCase())) return false;
        if (filtersClientes.dataInicio && c.createdAt < filtersClientes.dataInicio) return false;
        if (filtersClientes.dataFim && c.createdAt > filtersClientes.dataFim + 'T23:59:59') return false;
        return true;
      })
      .map(c => {
        const qtdeVencidas = allOverdueInvoices.filter(inv => inv.cliente_id === c.id).length;
        return {
          ...c,
          qtde_faturas_vencidas: qtdeVencidas
        };
      });
  }, [allClients, filtersClientes, allInvoices, allContracts]);

  const filteredContracts = useMemo(() => {
    return allContracts.filter((c) => {
      const s = filtersContratos.search.toLowerCase();
      if (s && !c.titulo?.toLowerCase().includes(s) && !String(c.id).includes(filtersContratos.search) && !c.cliente?.nome?.toLowerCase().includes(s)) return false;
      // em_dias e sem_faturas são filtrados pela API; inadimplente agora é checkbox
      if (
        filtersContratos.statusContrato &&
        !['em_dias', 'sem_faturas'].includes(filtersContratos.statusContrato) &&
        c.status !== filtersContratos.statusContrato
      )
        return false;

      if (filtersContratos.inadimplente) {
        // Um contrato é considerado inadimplente se o cliente tiver QUALQUER fatura vencida ligada ao contrato (ou ao cliente em geral no sistema)
        // No contexto deste relatório, vamos checar se o cliente_id está na lista de inadimplentes
        const clientHasOverdue = allOverdueInvoices.some(inv => inv.cliente_id === c.cliente_id);
        if (!clientHasOverdue) return false;
      }
      if (filtersContratos.estado && c.estado_inst !== filtersContratos.estado) return false;
      if (filtersContratos.cidade && !c.cidade_inst?.toLowerCase().includes(filtersContratos.cidade.toLowerCase())) return false;
      if (filtersContratos.bairro && !c.bairro_inst?.toLowerCase().includes(filtersContratos.bairro.toLowerCase())) return false;
      if (filtersContratos.vendedor && String(c.vendedor_id) !== filtersContratos.vendedor) return false;
      if (filtersContratos.dataInicio && c.data_contrato < filtersContratos.dataInicio) return false;
      if (filtersContratos.dataFim && c.data_contrato > filtersContratos.dataFim) return false;
      // Período data_bloqueio
      if (filtersContratos.dataBloqueioInicio) {
        const d = c.data_bloqueio ? String(c.data_bloqueio).substring(0, 10) : null;
        if (!d || d < filtersContratos.dataBloqueioInicio) return false;
      }
      if (filtersContratos.dataBloqueioFim) {
        const d = c.data_bloqueio ? String(c.data_bloqueio).substring(0, 10) : null;
        if (!d || d > filtersContratos.dataBloqueioFim) return false;
      }
      // Período data_instalacao
      if (filtersContratos.dataInstalacaoInicio) {
        const d = c.data_instalacao ? String(c.data_instalacao).substring(0, 10) : null;
        if (!d || d < filtersContratos.dataInstalacaoInicio) return false;
      }
      if (filtersContratos.dataInstalacaoFim) {
        const d = c.data_instalacao ? String(c.data_instalacao).substring(0, 10) : null;
        if (!d || d > filtersContratos.dataInstalacaoFim) return false;
      }
      // Período data_cancelamento
      if (filtersContratos.dataCancelamentoInicio) {
        const d = c.data_cancelamento ? String(c.data_cancelamento).substring(0, 10) : null;
        if (!d || d < filtersContratos.dataCancelamentoInicio) return false;
      }
      if (filtersContratos.dataCancelamentoFim) {
        const d = c.data_cancelamento ? String(c.data_cancelamento).substring(0, 10) : null;
        if (!d || d > filtersContratos.dataCancelamentoFim) return false;
      }
      // Período desbloqueio_confianca
      if (filtersContratos.desbloqueioConfiancaInicio) {
        const d = c.desbloqueio_confianca ? String(c.desbloqueio_confianca).substring(0, 10) : null;
        if (!d || d < filtersContratos.desbloqueioConfiancaInicio) return false;
      }
      if (filtersContratos.desbloqueioConfiancaFim) {
        const d = c.desbloqueio_confianca ? String(c.desbloqueio_confianca).substring(0, 10) : null;
        if (!d || d > filtersContratos.desbloqueioConfiancaFim) return false;
      }
      return true;
    });
  }, [allContracts, filtersContratos]);

  const filteredInvoices = useMemo(() => {
    return allInvoices.filter((inv) => {
      const s = filtersFaturas.search.toLowerCase();
      if (s && !String(inv.id).includes(filtersFaturas.search) && !inv.descricao?.toLowerCase().includes(s) && !inv.cliente?.nome?.toLowerCase().includes(s)) return false;
      if (filtersFaturas.statusFatura && inv.status !== filtersFaturas.statusFatura) return false;
      if (filtersFaturas.tipoFatura && inv.tipo !== filtersFaturas.tipoFatura) return false;
      if (filtersFaturas.dataInicio && inv.data_vencimento < filtersFaturas.dataInicio) return false;
      if (filtersFaturas.dataFim && inv.data_vencimento > filtersFaturas.dataFim) return false;
      if (filtersFaturas.dataPagamentoInicio) {
        const dp = inv.data_pagamento ? String(inv.data_pagamento).substring(0, 10) : null;
        if (!dp || dp < filtersFaturas.dataPagamentoInicio) return false;
      }
      if (filtersFaturas.dataPagamentoFim) {
        const dp = inv.data_pagamento ? String(inv.data_pagamento).substring(0, 10) : null;
        if (!dp || dp > filtersFaturas.dataPagamentoFim) return false;
      }
      if (filtersFaturas.dataCriacaoInicio) {
        const dc = (inv as any).createdAt ? String((inv as any).createdAt).substring(0, 10) : null;
        if (!dc || dc < filtersFaturas.dataCriacaoInicio) return false;
      }
      if (filtersFaturas.dataCriacaoFim) {
        const dc = (inv as any).createdAt ? String((inv as any).createdAt).substring(0, 10) : null;
        if (!dc || dc > filtersFaturas.dataCriacaoFim) return false;
      }
      return true;
    });
  }, [allInvoices, filtersFaturas]);

  const finSummary = useMemo(() => {
    if (invoicesResp?.summary) {
      const s = invoicesResp.summary;
      return {
        totalValor: Number(s.total_valor) || 0,
        totalPago: Number(s.total_pago) || 0,
        totalAberto: Number(s.total_aberto) || 0,
        totalVencido: Number(s.total_vencido) || 0,
        totalCancelado: Number(s.total_cancelado) || 0,
        total: Number(s.total_count) || 0,
      };
    }
    const fats = filteredInvoices;
    const totalValor = fats.reduce((a, f) => a + Number(f.valor || 0), 0);
    const totalPago = fats.reduce((a, f) => a + Number((f as any).valor_pago || 0), 0);
    const totalAberto = fats.filter((f) => f.status === 'aberto').reduce((a, f) => a + Number(f.valor || 0), 0);
    const totalVencido = fats.filter((f) => f.status === 'vencido').reduce((a, f) => a + Number(f.valor || 0), 0);
    const totalCancelado = fats.filter((f) => f.status === 'cancelado').reduce((a, f) => a + Number(f.valor || 0), 0);
    return { totalValor, totalPago, totalAberto, totalVencido, totalCancelado, total: fats.length };
  }, [filteredInvoices, invoicesResp?.summary]);

  const sortData = useCallback(
    <T,>(data: T[]): T[] => {
      return [...data].sort((a: any, b: any) => {
        let av = a[orderBy];
        let bv = b[orderBy];
        if (av === undefined || av === null) av = '';
        if (bv === undefined || bv === null) bv = '';
        if (typeof av === 'number' && typeof bv === 'number') {
          return orderDir === 'asc' ? av - bv : bv - av;
        }
        const sa = String(av).toLowerCase();
        const sb = String(bv).toLowerCase();
        if (sa < sb) return orderDir === 'asc' ? -1 : 1;
        if (sa > sb) return orderDir === 'asc' ? 1 : -1;
        return 0;
      });
    },
    [orderBy, orderDir]
  );

  const paginateData = useCallback(
    <T,>(data: T[]): T[] => data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage]
  );

  const handleSort = (col: string) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col);
      setOrderDir('asc');
    }
    setPage(0);
  };

  const exportCSV = useCallback(() => {
    let csv = '';
    let filename = '';
    if (reportType === 'clientes') {
      const isInadimplente = filtersClientes.inadimplente;
      csv = `ID;Nome;CPF/CNPJ;Tipo;Status${isInadimplente ? ';Qtde Faturas Vencidas' : ''};Cidade;Estado;Bairro;Data Cadastro\n`;
      filteredClients.forEach((c) => {
        csv += `${c.id};"${c.nome}";"${formatarCPFCNPJ(c.cpf_cnpj)}";"${c.tipo_pessoa === 'pf' ? 'PF' : 'PJ'}";"${c.status}"${isInadimplente ? `;${(c as any).qtde_faturas_vencidas || 0}` : ''};"${c.cidade}";"${c.estado}";"${c.bairro}";"${formatarData(c.createdAt)}"\n`;
      });
      filename = 'relatorio_clientes.csv';
    } else if (reportType === 'contratos') {
      const incluirQtdeFaturas = filtersContratos.inadimplente;
      csv = `ID;Título;Cliente;Vendedor;Status${incluirQtdeFaturas ? ';Qtde Faturas' : ''};Cidade Inst;Estado Inst;Data Contrato;Dia Vcto;Valor Contratado\n`;
      filteredContracts.forEach((c) => {
        csv += `${c.id};"${c.titulo}";"${c.cliente?.nome || ''}";"${c.vendedor?.nome || ''}";"${c.status}"${incluirQtdeFaturas ? `;${c.qtde_faturas_vencidas ?? 0}` : ''};"${c.cidade_inst}";"${c.estado_inst}";"${formatarData(c.data_contrato)}";${c.dia_vencimento};${c.valor_contratado || 0}\n`;
      });
      filename = 'relatorio_contratos.csv';
    } else if (reportType === 'faturas') {
      csv = 'ID;Cliente;Descrição;Tipo;Status;Valor;Valor Pago;Forma Pag.;Vencimento;Pagamento;Referência\n';
      filteredInvoices.forEach((f) => {
        const valorPago = (f as any).valor_pago || 0;
        csv += `${f.id};"${f.cliente?.nome || ''}";"${f.descricao}";"${f.tipo}";"${f.status}";${f.valor};${valorPago};"${f.forma_pagamento || ''}";"${formatarData(f.data_vencimento)}";"${f.data_pagamento ? formatarData(f.data_pagamento) : ''}";"${f.mes_referencia}"\n`;
      });
      filename = 'relatorio_faturas.csv';
    }
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }, [reportType, filteredClients, filteredContracts, filteredInvoices, finSummary]);

  const isLoading = reportType === 'clientes' ? loadingClients
    : reportType === 'contratos' ? loadingContracts
      : reportType === 'faturas' ? loadingInvoices
        : reportType === 'dici' || reportType === 'conexoes' || reportType === 'mensagens' || reportType === 'faturamento' ? false
          : loadingClients || loadingContracts || loadingInvoices;

  const handleRefresh = () => {
    if (reportType === 'clientes' || reportType === 'faturas') refetchClients();
    if (reportType === 'contratos' || reportType === 'faturas') refetchContracts();
    if (reportType === 'faturas') refetchInvoices();
  };

  const reportTabs: { key: ReportType; label: string; icon: React.ReactElement; color: string }[] = [
    { key: 'clientes', label: 'Clientes', icon: <PeopleAlt />, color: cores.primario },
    { key: 'contratos', label: 'Contratos', icon: <Assignment />, color: cores.ativo },
    { key: 'faturas', label: 'Faturas', icon: <Receipt />, color: cores.bloqueado },
    { key: 'faturamento', label: 'Faturamento', icon: <TrendingUp />, color: cores.ativo },
    { key: 'dici', label: 'DICI ANATEL', icon: <Router />, color: '#1976d2' },
    { key: 'conexoes', label: 'Conexões', icon: <NetworkCheck />, color: '#2e7d32' },
    { key: 'mensagens', label: 'Mensagens', icon: <Message />, color: '#7b1fa2' },
  ];

  const visibleReportTabs = useMemo(
    () => reportTabs.filter((tab) => can(REPORT_TAB_PERMISSIONS[tab.key])),
    [can, cores.primario, cores.ativo, cores.bloqueado],
  );

  useEffect(() => {
    if (visibleReportTabs.length === 0) return;
    if (!visibleReportTabs.some((tab) => tab.key === reportType)) {
      setReportType(visibleReportTabs[0].key);
    }
  }, [visibleReportTabs, reportType]);

  if (visibleReportTabs.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Sem permissão para relatórios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Você não possui permissão para acessar nenhuma aba de relatórios. Entre em contato com o administrador.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(cores.secundario, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(cores.primario, 0.12), color: cores.primario, width: 40, height: 40 }}>
              <Assessment sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Relatórios
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gere relatórios detalhados com filtros avançados
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {reportType !== 'dici' && reportType !== 'conexoes' && reportType !== 'mensagens' && reportType !== 'faturamento' && (
              <>
                <Tooltip title="Atualizar dados">
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton size="small" onClick={handleRefresh} disabled={isLoading} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      {isLoading ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Download sx={{ fontSize: 16 }} />}
                  onClick={exportCSV}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: theme.shadows[2] } }}
                >
                  Exportar CSV
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Tabs
          value={Math.max(0, visibleReportTabs.findIndex((t) => t.key === reportType))}
          onChange={(_e, idx) => {
            setReportType(visibleReportTabs[idx].key);
            setPage(0);
            setOrderBy('id');
            setOrderDir('desc');
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
          }}
        >
          {visibleReportTabs.map((tab) => (
            <Tab
              key={tab.key}
              icon={React.cloneElement(tab.icon, { sx: { fontSize: 17, color: reportType === tab.key ? tab.color : 'text.secondary' } })}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {tab.label}
                  {!isLoading && tab.key !== 'dici' && tab.key !== 'conexoes' && tab.key !== 'mensagens' && tab.key !== 'faturamento' && (
                    <Chip
                      label={
                        tab.key === 'clientes' ? fmtInt(filteredClients.length)
                          : tab.key === 'contratos' ? fmtInt(filteredContracts.length)
                            : tab.key === 'faturas' ? fmtInt(filteredInvoices.length)
                              : ''
                      }
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 18, bgcolor: alpha(tab.color, 0.12), color: tab.color, fontWeight: 700 }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {reportType === 'clientes' && (
        <RelatorioClientesTab
          data={filteredClients}
          filters={filtersClientes}
          onFilterChange={updateFiltersClientes}
          onClearFilters={clearFiltersClientes}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          orderDir={orderDir}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onSort={handleSort}
          sortData={sortData}
          paginateData={paginateData}
          onExportPdf={(ids) => relatorioPdfService.exportarClientes(ids, filtersClientes)}
        />
      )}
      {reportType === 'contratos' && (
        <RelatorioContratosTab
          data={filteredContracts}
          filters={filtersContratos}
          onFilterChange={updateFiltersContratos}
          onClearFilters={clearFiltersContratos}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          orderDir={orderDir}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onSort={handleSort}
          sortData={sortData}
          paginateData={paginateData}
          onExportPdf={(ids) => relatorioPdfService.exportarContratos(ids, filtersContratos)}
        />
      )}
      {reportType === 'faturas' && (
        <RelatorioFaturasTab
          data={filteredInvoices}
          filters={filtersFaturas}
          onFilterChange={updateFiltersFaturas}
          onClearFilters={clearFiltersFaturas}
          finSummary={finSummary}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          orderDir={orderDir}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onSort={handleSort}
          sortData={sortData}
          paginateData={paginateData}
          onExportPdf={(ids) => relatorioPdfService.exportarFaturas(ids, filtersFaturas)}
        />
      )}
      {reportType === 'faturamento' && (
        <RelatorioFaturamentoTab
          filters={filtersFaturamento}
          onFilterChange={updateFiltersFaturamento}
          onClearFilters={clearFiltersFaturamento}
        />
      )}
      {reportType === 'dici' && <RelatorioDiciTab />}
      {reportType === 'conexoes' && <RelatorioConexoesTab />}
      {reportType === 'mensagens' && <RelatorioMensagensTab />}
    </Box>
  );
};

export default RelatoriosPage;
